/**
 * Global drawer controller.
 *
 * Any detail panel can call `openCrmChatDrawer({ ... })` to surface Ask Type+
 * in a docked right side panel (desktop) with a prefilled record + optional
 * starter prompt. The controller mounts a single `<CrmChatDrawer>` near the
 * app root so callers don't need portals or context plumbing.
 */
import React, { useEffect, useState } from "react";

import CrmChatDrawer from "./components/CrmChatDrawer";
import type { CrmChatRecordContext, CrmRecordType } from "./types/chat.types";

const OPEN_EVENT = "crm-chat-drawer:open";
const CLOSE_EVENT = "crm-chat-drawer:close";

export interface OpenCrmChatDrawerOptions {
  workspaceId: string;
  recordId?: string;
  recordType?: CrmRecordType;
  displayName?: string;
  subtitle?: string;
  avatarUrl?: string;
  promptType?: string;
}

interface DrawerState {
  isOpen: boolean;
  workspaceId: string | null;
  record: CrmChatRecordContext | null;
  promptType: string | null;
}

/** Imperative API for opening the chat drawer from anywhere in the app. */
export function openCrmChatDrawer(options: OpenCrmChatDrawerOptions): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<OpenCrmChatDrawerOptions>(OPEN_EVENT, { detail: options }));
}

/** Imperative API for closing the chat drawer. */
export function closeCrmChatDrawer(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CLOSE_EVENT));
}

const CrmChatController: React.FC = () => {
  const [state, setState] = useState<DrawerState>({
    isOpen: false,
    workspaceId: null,
    record: null,
    promptType: null,
  });

  useEffect(() => {
    const handleOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenCrmChatDrawerOptions>).detail;
      if (!detail || !detail.workspaceId) return;

      const record: CrmChatRecordContext | null =
        detail.recordId && detail.recordType
          ? {
              recordId: detail.recordId,
              recordType: detail.recordType,
              displayName: detail.displayName ?? detail.recordId,
              subtitle: detail.subtitle,
              avatarUrl: detail.avatarUrl,
            }
          : null;

      setState({
        isOpen: true,
        workspaceId: detail.workspaceId,
        record,
        promptType: detail.promptType ?? null,
      });
    };

    const handleClose = () => {
      setState((prev) => ({ ...prev, isOpen: false }));
    };

    window.addEventListener(OPEN_EVENT, handleOpen);
    window.addEventListener(CLOSE_EVENT, handleClose);
    return () => {
      window.removeEventListener(OPEN_EVENT, handleOpen);
      window.removeEventListener(CLOSE_EVENT, handleClose);
    };
  }, []);

  if (!state.workspaceId) return null;

  return (
    <CrmChatDrawer
      isOpen={state.isOpen}
      onClose={() => setState((prev) => ({ ...prev, isOpen: false }))}
      workspaceId={state.workspaceId}
      initialRecord={state.record ?? undefined}
      initialPromptType={state.promptType ?? undefined}
    />
  );
};

export default CrmChatController;
